import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getUploadUrl } from '../../config/api';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    companyName: '',
    phone: '',
    rut: ''
  });
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editPassword, setEditPassword] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/admin/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCreatePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCreatePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCreatePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      setUploading(true);
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('role', formData.role);
      formDataToSend.append('companyName', formData.companyName || '');
      formDataToSend.append('phone', formData.phone || '');
      formDataToSend.append('rut', formData.rut);
      
      if (createPhoto) {
        formDataToSend.append('photo', createPhoto);
      }

      await axios.post('/api/admin/users', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess('Usuario creado exitosamente');
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        companyName: '',
        phone: '',
        rut: ''
      });
      setCreatePhoto(null);
      setCreatePhotoPreview(null);
      setShowCreateForm(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Error al crear usuario');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditPassword('');
    setEditEmail(user.email || '');
    setEditPhone(user.phone || '');
    setEditPhoto(null);
    setEditPhotoPreview(user.photo?.filename ? getUploadUrl(user.photo.filename) : null);
    setError('');
  };

  const handleCloseEdit = () => {
    setEditingUser(null);
    setEditPassword('');
    setEditEmail('');
    setEditPhone('');
    setEditPhoto(null);
    setEditPhotoPreview(null);
    setError('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUploading(true);

    // Validate email format
    if (editEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editEmail)) {
      setError('Por favor ingrese un correo electrónico válido');
      setUploading(false);
      return;
    }

    if (editPassword && editPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setUploading(false);
      return;
    }

    if (!editPassword && !editPhoto && editEmail === editingUser.email && editPhone === editingUser.phone) {
      setError('Debe proporcionar al menos un campo para actualizar');
      setUploading(false);
      return;
    }

    try {
      const formData = new FormData();
      if (editPassword) {
        formData.append('password', editPassword);
      }
      if (editEmail) {
        formData.append('email', editEmail.trim());
      }
      if (editPhone !== undefined) {
        formData.append('phone', editPhone.trim());
      }
      if (editPhoto) {
        formData.append('photo', editPhoto);
        console.log('Uploading photo:', editPhoto.name, editPhoto.type, editPhoto.size);
      }

      console.log('Sending update request for user:', editingUser._id);
      const response = await axios.put(`/api/admin/users/${editingUser._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Update response:', response.data);
      setSuccess('Usuario actualizado exitosamente');
      handleCloseEdit();
      // Refresh users list after a short delay to ensure file is saved
      setTimeout(() => {
        fetchUsers();
      }, 500);
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Error al actualizar usuario');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        await axios.delete(`/api/admin/users/${id}`);
        setSuccess('Usuario eliminado exitosamente');
        fetchUsers();
      } catch (error) {
        setError(error.response?.data?.message || 'Error al eliminar usuario');
      }
    }
  };

  if (loading) {
    return <div className="loading">Cargando usuarios...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Gestión de Usuarios</h2>
        <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
          Agregar Nuevo Usuario
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showCreateForm && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3>Crear Nuevo Usuario</h3>
          <form onSubmit={handleCreateUser}>
            <div className="form-group">
              <label>Nombre *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Correo Electrónico *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Contraseña *</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label>Rol *</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="user">Usuario (Empresa de Transporte)</option>
                <option value="admin">Administrador</option>
              </select>
            </div>

            <div className="form-group">
              <label>Nombre de la Empresa</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>RUT (Número Único de Empresa) *</label>
              <input
                type="text"
                name="rut"
                value={formData.rut}
                onChange={handleChange}
                placeholder="Ej: 12345678-9"
                required
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Foto de Perfil</label>
              {createPhotoPreview && (
                <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                  <img 
                    src={createPhotoPreview} 
                    alt="Preview" 
                    style={{
                      maxWidth: '150px',
                      maxHeight: '150px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}
              <div style={{
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                backgroundColor: createPhoto ? '#f0f9ff' : '#f9fafb'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = createPhoto ? '#f0f9ff' : '#f9fafb';
              }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCreatePhotoChange}
                  style={{ display: 'none' }}
                  id="create-photo-upload"
                />
                <label htmlFor="create-photo-upload" style={{ cursor: 'pointer', display: 'block' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
                  <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                    {createPhoto ? createPhoto.name : 'Haga clic para seleccionar una foto'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    JPG, PNG o GIF (máx. 5MB)
                  </div>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="submit" 
                className="btn btn-success"
                disabled={uploading}
              >
                {uploading ? 'Creando...' : 'Crear Usuario'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowCreateForm(false);
                  setFormData({
                    name: '',
                    email: '',
                    password: '',
                    role: 'user',
                    companyName: '',
                    phone: '',
                    rut: ''
                  });
                  setCreatePhoto(null);
                  setCreatePhotoPreview(null);
                  setError('');
                }}
                disabled={uploading}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {users.length === 0 ? (
        <div className="card">
          <p>No se encontraron usuarios.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="table responsive-table">
            <thead>
              <tr>
                <th>Foto</th>
                <th>Nombre</th>
                <th>Correo Electrónico</th>
                <th>Rol</th>
                <th>Empresa</th>
                <th>RUT</th>
                <th>Phone</th>
                <th>Fecha de Creación</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user._id}>
                  <td data-label="Foto">
                    {user.photo?.filename ? (
                      <img 
                        src={getUploadUrl(user.photo.filename)} 
                        alt={user.name}
                        crossOrigin="anonymous"
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #e5e7eb',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>Sin foto</span>
                    )}
                  </td>
                  <td data-label="Nombre">{user.name}</td>
                  <td data-label="Correo Electrónico">{user.email}</td>
                  <td data-label="Rol">
                    <span className={`badge badge-${user.role === 'admin' ? 'approved' : 'pending'}`}>
                      {user.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </span>
                  </td>
                  <td data-label="Empresa">{user.companyName || 'N/A'}</td>
                  <td data-label="RUT">{user.rut || 'N/A'}</td>
                  <td data-label="Phone">{user.phone || 'N/A'}</td>
                  <td data-label="Fecha de Creación">{new Date(user.createdAt).toLocaleDateString('es-ES')}</td>
                  <td data-label="Acciones">
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => handleEdit(user)}
                        style={{ padding: '5px 10px', fontSize: '14px', flex: '1', minWidth: '80px' }}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDelete(user._id)}
                        style={{ padding: '5px 10px', fontSize: '14px', flex: '1', minWidth: '80px' }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            position: 'relative',
            border: '1px solid #e5e7eb'
          }}>
            <button
              onClick={handleCloseEdit}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#dc2626';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#ef4444';
                e.target.style.transform = 'scale(1)';
              }}
            >
              ×
            </button>

            <h3 style={{ 
              marginBottom: '24px', 
              color: '#1e40af',
              fontSize: '24px',
              fontWeight: '600',
              paddingRight: '40px'
            }}>
              ✏️ Editar Usuario
            </h3>

            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Nombre:</strong> {editingUser.name}
              </p>
              <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                <strong>Email:</strong> {editingUser.email}
              </p>
            </div>

            {error && <div className="alert alert-error" style={{ marginBottom: '20px' }}>{error}</div>}

            <form onSubmit={handleUpdateUser}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Correo Electrónico *
                </label>
                <input
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="Ingrese correo electrónico"
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="Ingrese número de teléfono"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Nueva Contraseña (dejar vacío para no cambiar)
                </label>
                <input
                  type="password"
                  value={editPassword}
                  onChange={(e) => setEditPassword(e.target.value)}
                  placeholder="Ingrese nueva contraseña"
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    transition: 'all 0.2s',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Foto de Perfil
                </label>
                {editPhotoPreview && (
                  <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                    <img 
                      src={editPhotoPreview} 
                      alt="Preview" 
                      style={{
                        maxWidth: '150px',
                        maxHeight: '150px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                {!editPhotoPreview && editingUser?.photo?.filename && (
                  <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                    <img 
                      src={getUploadUrl(editingUser.photo.filename)} 
                      alt="Current photo" 
                      style={{
                        maxWidth: '150px',
                        maxHeight: '150px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <div style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  padding: '20px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: editPhoto ? '#f0f9ff' : '#f9fafb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = editPhoto ? '#f0f9ff' : '#f9fafb';
                }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ display: 'none' }}
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                      {editPhoto ? editPhoto.name : 'Haga clic para seleccionar una foto'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                      JPG, PNG o GIF (máx. 5MB)
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseEdit}
                  disabled={uploading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: uploading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading) {
                      e.target.style.backgroundColor = '#4b5563';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading) {
                      e.target.style.backgroundColor = '#6b7280';
                    }
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={uploading || (!editPassword && !editPhoto && !editEmail && !editPhone)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: (!editPassword && !editPhoto && !editEmail && !editPhone) || uploading ? '#9ca3af' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: (!editPassword && !editPhoto && !editEmail && !editPhone) || uploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!uploading && (editPassword || editPhoto || editEmail || editPhone)) {
                      e.target.style.backgroundColor = '#2563eb';
                      e.target.style.transform = 'translateY(-1px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!uploading && (editPassword || editPhoto || editEmail || editPhone)) {
                      e.target.style.backgroundColor = '#3b82f6';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  {uploading ? 'Guardando...' : '💾 Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
